import { useEffect, useState } from "react";

export function useGeolocation(options) {
  const [position, setPosition] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let watchId;
    let permissionStatus;
    let cancelled = false;

    if (!("geolocation" in navigator)) {
      setError(new Error("Geolocalización no soportada por este navegador"));
      setLoading(false);
      return;
    }

    const onSuccess = (pos) => {
      if (cancelled) return;
      const { latitude, longitude, accuracy } = pos.coords;
      setPosition({
        latitude,
        longitude,
        accuracy,
        timestamp: pos.timestamp,
      });
      setError(null);
      setLoading(false);
    };

    const onError = (err) => {
      if (cancelled) return;
      setError(err);
      setLoading(false);
    };

    // Check permission before accessing geolocation
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((status) => {
        permissionStatus = status;
        if (status.state === 'granted') {
          watchId = navigator.geolocation.watchPosition(onSuccess, onError, options);
        } else if (status.state === 'prompt') {
          // Try to get permission by prompting user
          watchId = navigator.geolocation.watchPosition(onSuccess, onError, options);
        } else if (status.state === 'denied') {
          setError(new Error('Permiso de geolocalización denegado por el usuario o navegador.'));
          setLoading(false);
        }
        // Listen for permission changes
        status.onchange = () => {
          if (status.state === 'granted') {
            setLoading(true);
            watchId = navigator.geolocation.watchPosition(onSuccess, onError, options);
          } else if (status.state === 'denied') {
            setError(new Error('Permiso de geolocalización denegado por el usuario o navegador.'));
            setLoading(false);
            setPosition(null);
            if (watchId) navigator.geolocation.clearWatch(watchId);
          }
        };
      }).catch(() => {
        // Fallback if permissions API fails
        watchId = navigator.geolocation.watchPosition(onSuccess, onError, options);
      });
    } else {
      // Fallback for browsers without permissions API
      watchId = navigator.geolocation.watchPosition(onSuccess, onError, options);
    }

    return () => {
      cancelled = true;
      if (watchId) navigator.geolocation.clearWatch(watchId);
      if (permissionStatus && permissionStatus.onchange) permissionStatus.onchange = null;
    };
  }, [options]);

  const getCurrent = () =>
    new Promise((resolve, reject) => {
      if (!("geolocation" in navigator))
        return reject(new Error("Geolocalización no soportada"));
      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: 'geolocation' }).then((status) => {
          if (status.state === 'granted' || status.state === 'prompt') {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const { latitude, longitude, accuracy } = pos.coords;
                resolve({
                  latitude,
                  longitude,
                  accuracy,
                  timestamp: pos.timestamp,
                });
              },
              reject,
              options
            );
          } else {
            reject(new Error('Permiso de geolocalización denegado por el usuario o navegador.'));
          }
        }).catch(() => {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const { latitude, longitude, accuracy } = pos.coords;
              resolve({
                latitude,
                longitude,
                accuracy,
                timestamp: pos.timestamp,
              });
            },
            reject,
            options
          );
        });
      } else {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude, accuracy } = pos.coords;
            resolve({
              latitude,
              longitude,
              accuracy,
              timestamp: pos.timestamp,
            });
          },
          reject,
          options
        );
      }
    });

  return { position, loading, error, getCurrent };
}
